import { AfterViewChecked, ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { CustomValidators } from '../../../helpers/CustomValidators';
import { KeyValueModel } from '../../../models/misc/key-value/key-value.model';
import { DialogRef } from '@fundamental-ngx/core/dialog';
import { NotificationsService } from '../../../../service/services/misc/notifications/notifications.service';
import { Utils } from '../../../helpers/Utils';
import {PolicyAction, ResourceType} from '../../../helpers/Const';
import { SwaService } from '../../../../service/services/misc/swa/swa.service';
import { AddLabelsAndAnnotationsComponent } from '../../../../features/organizations/dialogs/shared/add-labels-and-annotations/add-labels-and-annotations.component';
import {RuleModel} from "../../../models/misc/rule.model";
import {DialogService} from "../../../../service/services/misc/dialog/dialog.service";
import {ResourceModel} from "../../../models/resources/resource.model";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {SideDialogComponent} from "../../../../shared/components/side-dialog/side-dialog.component";

@Component({
    template: '',
    standalone: false
})

export abstract class BaseCreateDialogComponent implements OnInit, AfterViewChecked {
    Utils = Utils;
    ResourceType = ResourceType;
    resource: ResourceModel;
    resourceName: ResourceType;
    resourceDisplayName: string;
    LABELS_MATCH_REGEX = /.+\/labels/;
    ANNOTATIONS_MATCH_REGEX = /.+\/annotations/;
    ALL_MATCH_REGEX = /^.*\/\*$/;

    @HostListener('document:keypress', ['$event']) onKeyPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            if (this.validateForm()) {
                this.onSubmit();
            }
        }
    }

    labels: KeyValueModel[] = [];
    annotations: KeyValueModel[] = [];

    isEdit = true;

    constraintType: string;

    errorMsg: string;
    loading: boolean;

    isNonSubresourceActionsEnabled = true;
    isLabelsEnabled = true;
    isAnnotationEnabled = true;
    rules: RuleModel[];
    subresourceRules: RuleModel[];

    form = this.fb.group({
	    name: [
	        '',
	        { validators: [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName] },
	    ],
	    displayName: [
	        '',
	        { validators: [Validators.required, Validators.maxLength(100), CustomValidators.ValidateDisplayName] },
	    ],
	    description: ['', { validators: [Validators.maxLength(250), CustomValidators.ValidateDescription] }],
    });

    @ViewChild('addLabelsAndAnnotationsComponent')
	    addLabelsAndAnnotationsComponent: AddLabelsAndAnnotationsComponent;

    constructor(
		public dialogRef: DialogRef,
    public dialogService: DialogService,
		protected notificationsService: NotificationsService,
		protected fb: UntypedFormBuilder,
		protected cdRef: ChangeDetectorRef,
		protected _swaService: SwaService,
		protected _uisService: UisService
    ) {}

    ngOnInit(): void {
	    this.isEdit = this.dialogRef.data.isEdit;
	    this.constraintType = this.isEdit ? 'edit' : 'create';
	    this.initDialog();
    }

    initDialog() {
	    const data = this.dialogRef.data;
	    this.resource = data.resource;
        this.initSubresourceEnablement(data.resource?.rules);
	    if (this.isEdit) {
	        this.form.patchValue(data.resource);
	        const rawLabels = data.resource.labels;
	        const rawAnnotations = data.resource.annotations;
	        this.labels = this.convertRawLabelsToArray(rawLabels);
	        this.annotations = this.convertRawLabelsToArray(rawAnnotations);
	    }
	    if (this.labels.length === 0) {
	        this.labels.push(new KeyValueModel('', ''));
	    }
	    if (this.annotations.length === 0) {
	        this.annotations.push(new KeyValueModel('', ''));
	    }
	    this._swaService.sendCustomEvent(
	        this.isEdit? this._swaService.SWA_EVENT_TYPES.OPEN_EDIT_RESOURCE_FORM :
	            this._swaService.SWA_EVENT_TYPES.OPEN_CREATE_RESOURCE_FORM,
	        [data?.resource?.type],
	        []
	    );
    }

    initSubresourceEnablement(rules: RuleModel[] = []) {
        this.rules = rules.filter(p => p.type.split('/').length === 1);
        this.subresourceRules = rules.filter(p => p.type.split('/').length > 1);

        const resourceUpdateRules = this.rules.filter((r: any) => r.action === PolicyAction.Update || r.action === PolicyAction.Patch || r.action === PolicyAction.All);
        const subresourceUpdateRules = this.subresourceRules.filter((r: any) => r.action === PolicyAction.Update || r.action === PolicyAction.Patch || r.action === PolicyAction.All);
        const isAllSubresourceActionsEnabled = this.isTypeMatch(subresourceUpdateRules, this.ALL_MATCH_REGEX)
				|| resourceUpdateRules.some(r => r.type === "*");
        this.isLabelsEnabled = isAllSubresourceActionsEnabled || this.isTypeMatch(subresourceUpdateRules, this.LABELS_MATCH_REGEX);
        this.isAnnotationEnabled = isAllSubresourceActionsEnabled || this.isTypeMatch(subresourceUpdateRules, this.ANNOTATIONS_MATCH_REGEX);
        this.isNonSubresourceActionsEnabled = !this.isEdit || this.rules.some(r => r.action === PolicyAction.Update || r.action === PolicyAction.All);
    }

    isTypeMatch(rules: RuleModel[], regex: RegExp) {
        return rules.some(r => regex.test(r.type));
    }

    sendEvent(type: string, name: string, isEdit: boolean | undefined) {
	    this._swaService.sendCustomEvent(
	        isEdit? this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM :
	            this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM,
	        [type],
	        []
	    );
    }

    convertRawLabelsToArray(rawLabels: any): KeyValueModel[] {
	    if (!rawLabels) {
	        return [];
	    }
	    return Object.keys(rawLabels).map((key) => new KeyValueModel(key, rawLabels[key]));
    }

    ngAfterViewChecked() {
	    this.cdRef.detectChanges();
    }

    setLoading(isLoading: boolean) {
	    this.loading = isLoading;
	    this.dialogRef.loading(isLoading);
    }

    validateLabelsAndAnnotations() {
	    if (!this.addLabelsAndAnnotationsComponent) {
	        return true;
	    }

	    return (
	        this.addLabelsAndAnnotationsComponent?.validateLabels() &&
			this.addLabelsAndAnnotationsComponent?.validateAnnotations()
	    );
    }

    validateForm() {
	    return this.form.valid && this.validateLabelsAndAnnotations() && !this.loading;
    }

    onSuccess(res: any) {
	    const msg = (this.isEdit ? 'Updating resource ' : 'Creating resource ') + res.name + ' (' + res.type + ')...';
	    this.notificationsService.showMessageToast(msg);
	    this.dialogRef.close(res);

	    this._swaService.sendCustomEvent((this.isEdit ? this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_SUCCESS :
	        this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_SUCCESS), [res.type], []);
    }

    onError(dialog: SideDialogComponent, error: Error) {
	    this.errorMsg = error.message;
	    this._swaService.sendCustomEvent((this.isEdit ? this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_FAILED :
	        this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_FAILED) + " - " + this.dialogRef?.data?.resource?.type,
	    [error.message], []);

	    this.setLoading(false);
	    this.cdRef.detectChanges();
	    dialog.focusErrorStrip();
    }

    onSubmit() {}
}
